import { clampToRange } from "../../../../utils/censorshipGeometry";

export type TOverlayPlacement = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

const createDuplicateId = (
  placements: TOverlayPlacement[],
  sourceId: string,
) => {
  const existingIds = new Set(placements.map(({ id }) => id));
  const copyId = `${sourceId}-copy`;
  if (!existingIds.has(copyId)) return copyId;

  let copyNumber = 2;
  while (existingIds.has(`${copyId}-${copyNumber}`)) copyNumber += 1;
  return `${copyId}-${copyNumber}`;
};

const offsetWithinBounds = (position: number, size: number, offset: number) => {
  const maximum = Math.max(0, 1 - size);
  const forwardPosition = position + offset;
  if (forwardPosition <= maximum) return forwardPosition;
  return clampToRange(position - offset, 0, maximum);
};

export const duplicateOverlayPlacement = <T extends TOverlayPlacement>({
  placements,
  sourceId,
  offset,
}: {
  placements: T[];
  sourceId: string;
  offset: { x: number; y: number };
}): { placements: T[]; duplicatedPlacement: T | null } => {
  const source = placements.find(({ id }) => id === sourceId);
  if (!source) return { placements, duplicatedPlacement: null };

  const duplicatedPlacement = {
    ...source,
    id: createDuplicateId(placements, source.id),
    x: offsetWithinBounds(source.x, source.width, offset.x),
    y: offsetWithinBounds(source.y, source.height, offset.y),
  };

  return {
    placements: [...placements, duplicatedPlacement],
    duplicatedPlacement,
  };
};

export const removeOverlayPlacement = <T extends TOverlayPlacement>(
  placements: T[],
  placementId: string,
) => placements.filter(({ id }) => id !== placementId);
